/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment, @typescript-eslint/no-non-null-assertion, react/no-children-prop */
import React, { Suspense } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import DirectedAcyclicGraph from './DirectedAcyclicGraph';
import { WEB_APP_PATH } from 'stash/constants';
import { isProd } from 'stash/utils';
import { Route, Switch } from 'react-router-dom';
import { selectSignUpState } from 'stash/selectors';
import SignUpTransition from './SignUpTransition';
import { Step, Graph, Options } from './types';

const isProduction = isProd();

export const mapStateToProps = (state: any) => {
    return {
        store: selectSignUpState(state),
    };
};

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type OwnProps = {
    product?: string;
    graph: Graph;
    options?: Options;
    webAppPath?: string;
};

export type SignUpGraphComponentProps = OwnProps;

type SignUpGraphProps = PropsFromRedux & OwnProps & RouteComponentProps;

interface SignUpGraphState {
    currentStep: any;
    graphSteps: Record<string, Step>;
}

export class SignUpGraph extends React.Component<SignUpGraphProps, SignUpGraphState> {
    private signUpPrefix: string;
    private paths: Record<string, string>;
    private preloadedSteps: Record<string, boolean>;
    private graph: DirectedAcyclicGraph;

    static defaultProps: Partial<SignUpGraphProps> = {
        webAppPath: WEB_APP_PATH,
        options: {
            quiet: isProduction, // suppress graph errors on prod
            cyclic: isProduction, // suppress cycle checks on prod
        },
    };

    constructor(props: SignUpGraphProps) {
        super(props);

        this.getLatestStep = this.getLatestStep.bind(this);
        this.getNextStep = this.getNextStep.bind(this);
        this.getPathToSteps = this.getPathToSteps.bind(this);
        this.completeStep = this.completeStep.bind(this);
        this.navigateToNextPath = this.navigateToNextPath.bind(this);
        this._preloadAllSteps = this._preloadAllSteps.bind(this);
        this._preloadNextSteps = this._preloadNextSteps.bind(this);

        this.signUpPrefix = window.location.pathname.split('/')[1] || 'sign-up';
        this.paths = this.getPathToSteps();

        this.state = {
            currentStep: {},
            graphSteps: props.graph.steps,
        };

        this.preloadedSteps = {};

        this.graph = new DirectedAcyclicGraph(
            Object.keys(this.props.graph.steps),
            // @ts-ignore TODO type DirectedAcyclicGraph.jsx
            props.graph.transitions,
            {
                quiet: props.options?.quiet || false,
                cyclic: props.options?.cyclic || false,
            }
        );
    }

    componentDidMount(): void {
        const { history } = this.props;
        const currentStep = this.getLatestStep();

        const currentScreen = this.props.graph.steps[currentStep];
        const resumePath = `/${this.signUpPrefix}${currentScreen.path}`;

        this.setState({
            currentStep,
        });

        if (history.location.pathname !== resumePath) {
            history.push(resumePath);
        }
    }

    _preloadAllSteps(): void {
        const { graphSteps } = this.state;

        Object.keys(graphSteps).forEach((stepName) => {
            const step = graphSteps[stepName];

            if (!this.preloadedSteps[stepName] && (step?.component as any)?.preload) {
                this.preloadedSteps[stepName] = true;
                (step.component as any).preload();
            }
        });
    }

    _preloadNextSteps(nextNode: string): void {
        const { graphSteps } = this.state;

        // @ts-ignore TODO: type DirectedAcyclicGraph.jsx
        const nextGraphNode = this.graph.graph[nextNode];
        if (nextGraphNode) {
            nextGraphNode.transitions.forEach((transition: any) => {
                const stepName = transition.nextNode;
                const stepNode = graphSteps[stepName];

                if (!this.preloadedSteps[stepName] && (stepNode?.component as any)?.preload) {
                    this.preloadedSteps[stepName] = true;
                    (stepNode.component as any).preload();
                }
            });
        }
    }

    getLatestStep(): string {
        const nextNode = this.graph.getLatestNode(this.props.store);
        this._preloadNextSteps(nextNode);
        return nextNode;
    }

    getNextStep(): string {
        const { store, location } = this.props;
        const currentLocationStep = '/' + location.pathname.split('/').pop();
        const currentStep = this.paths[currentLocationStep];
        const nextNode = this.graph.getNextNode(store, currentStep);

        this._preloadNextSteps(nextNode);
        return nextNode;
    }

    getPathToSteps(): Record<string, string> {
        return Object.keys(this.props.graph.steps).reduce((acc, step) => {
            return {
                ...acc,
                [this.props.graph.steps[step].path]: step,
            };
        }, {});
    }

    navigateToNextPath(nextPath: string): void {
        const { history } = this.props;

        // Scroll back up for small-screen mobile devices.
        window.scrollY && window.scrollTo && window.scrollTo(0, 0);
        history.push(nextPath);
    }

    completeStep(options: { nextGraph?: { step: string; path: string }; redirectToWebApp?: boolean }): void {
        const { nextGraph, redirectToWebApp } = options;
        const { webAppPath } = this.props;

        if (redirectToWebApp) {
            window.location.href = webAppPath!;
        } else {
            // Wait for all redux actions in callstack to complete.
            window.setTimeout(() => {
                if (nextGraph && nextGraph.step && nextGraph.path) {
                    return this.setState({ currentStep: nextGraph.step }, () => {
                        this.navigateToNextPath(`/${this.signUpPrefix}${nextGraph.path}`);
                    });
                }

                const nextStep = this.getNextStep();

                this.setState({ currentStep: nextStep }, () => {
                    const nextScreen = this.props.graph.steps[nextStep];
                    const nextPath = `/${this.signUpPrefix}${nextScreen.path}`;
                    this.navigateToNextPath(nextPath);
                });
            }, 0);
        }
    }

    render(): React.ReactElement {
        const { graphSteps } = this.state;
        const { location, product } = this.props;
        return (
            // @ts-ignore - SignUpTransition has typing issues
            <SignUpTransition location={location} children={
                <Suspense fallback={<div></div>}>
                    <section>
                        <Switch location={location}>
                            {Object.values(graphSteps).map((step) => {
                                return (
                                    <Route exact key={step.path} path={`/sign-up*${step.path}`}>
                                        <step.component
                                            product={product}
                                            location={location}
                                            {...step.props}
                                            completeStep={() => {
                                                this.completeStep({
                                                    nextGraph: step.nextGraph,
                                                    redirectToWebApp: !!step.redirectToWebApp,
                                                });
                                            }}
                                        />
                                    </Route>
                                );
                            })}
                        </Switch>
                    </section>
                </Suspense>
            } />
        );
    }
}

export default connector(withRouter(SignUpGraph as any)); 